import Checkbox, { CheckboxProps } from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  PathValue,
} from "react-hook-form";

import { setControllerFieldRef } from "../../helpers/form";
import { getNestedProperty } from "../../helpers/object";
import {
  ValidationOptions,
  validationOptionsToControlRules,
} from "../../types/Validations";

interface APCheckboxProps<T extends FieldValues> {
  name: Path<T>;
  label?: string;
  validations?: ValidationOptions;
  errors?: FieldErrors<T>;
  control?: Control<T>;
  defaultValue: PathValue<T, Path<T>> | undefined;
  value?: boolean;
  showRequired?: boolean;
  hideRequired?: boolean;
  highlightOnEmpty?: boolean;
  readOnly?: boolean;
  onChange?: (value: boolean) => void;
  stopPropogation?: boolean;
  disabled?: boolean;
}

const APCheckbox = <T extends FieldValues>({
  name,
  label,
  validations,
  errors,
  control,
  defaultValue,
  value,
  showRequired,
  hideRequired,
  highlightOnEmpty,
  readOnly,
  onChange,
  stopPropogation,
  disabled,
}: APCheckboxProps<T>) => {
  const error = errors && getNestedProperty(name, errors);
  const renderCheckbox = (
    value: boolean,
    props: CheckboxProps,
    onChange?: (value: boolean) => void
  ) => {
    return (
      <FormControlLabel
        sx={{
          mx: 0,
          color: highlightOnEmpty && !value ? "error.main" : "",
        }}
        label={showRequired && !hideRequired ? `${label} *` : label}
        onClick={(e) => {
          if (!onChange) {
            e.preventDefault();
          } else if (stopPropogation) {
            e.stopPropagation();
          }
        }}
        control={
          <Checkbox
            {...props}
            required={!hideRequired && validations?.required}
            sx={{
              p: 0,
              mr: label ? 1 : 0,
            }}
            checked={value}
            onChange={(e) => onChange?.(e.target.checked)}
            onClick={(e) => {
              if (stopPropogation) {
                e.stopPropagation();
              }
            }}
            readOnly={readOnly}
            disabled={readOnly || disabled}
          />
        }
      />
    );
  };

  return (
    <FormControl error={!!errors?.[name]}>
      {control ? (
        <Controller
          name={name}
          control={control}
          defaultValue={defaultValue}
          rules={
            validations
              ? validationOptionsToControlRules(label ?? "", validations)
              : undefined
          }
          render={({ field: { ref, onChange: fieldOnChange, value, ...field } }) =>
            renderCheckbox(
              value,
              {
                ...field,
                inputRef: (el) => setControllerFieldRef(ref, el),
              },
              fieldOnChange
            )
          }
        />
      ) : (
        renderCheckbox(value ?? false, {}, onChange)
      )}
      {!!error && (
        <FormHelperText sx={{ ml: 0 }}>{`${error?.message}`}</FormHelperText>
      )}
    </FormControl>
  );
};

export default APCheckbox;
